import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css';
import { supabase } from './supabase';
import { useCart } from './CartContext';

function BooksCheckout() {
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ 
    name: '', 
    email: '', 
    mobile: '', 
    address: '',
    city: '',
    country: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const paypalRef = useRef(null);
  const paypalButtonsRef = useRef(null);

  useEffect(() => {
    if (cart.length === 0) {
      navigate('/books');
    }
  }, [cart, navigate]);

  useEffect(() => {
    if (cart.length === 0) return;
    
    // Clear any existing PayPal buttons
    if (paypalRef.current) {
      paypalRef.current.innerHTML = '';
    }
    
    // Clean up previous buttons instance
    if (paypalButtonsRef.current) {
      paypalButtonsRef.current = null;
    }
    
    const loadPayPal = () => {
      if (window.paypal && paypalRef.current && !paypalButtonsRef.current) {
        const total = getCartTotal();
        
        paypalButtonsRef.current = window.paypal.Buttons({
          createOrder: (data, actions) => {
            return actions.order.create({
              purchase_units: [{
                amount: {
                  value: total.toFixed(2),
                  currency_code: 'USD'
                },
                description: `Books Order - ${cart.length} item(s)`
              }]
            });
          },
          onApprove: async (data, actions) => {
            try {
              const order = await actions.order.capture();
              
              const orderId = `ORD-${Date.now()}`;
              const total = getCartTotal();
              
              // Get current form values
              const currentForm = { 
                name: form.name, 
                email: form.email, 
                mobile: form.mobile,
                address: form.address,
                city: form.city,
                country: form.country
              };
              
              // Create order for each book in cart with PayPal payment
              const orderPromises = cart.map((item) => {
                const bookOrderData = {
                  order_id: orderId,
                  customer_name: currentForm.name || 'PayPal Customer',
                  email: currentForm.email || order.payer.email_address,
                  mobile: currentForm.mobile || '',
                  address: currentForm.address || '',
                  city: currentForm.city || '',
                  country: currentForm.country || '',
                  book_title: item.title,
                  book_key: item.key,
                  book_image_url: item.img,
                  quantity: item.quantity,
                  unit_price: item.price,
                  subtotal: item.price * item.quantity,
                  total_amount: total,
                  payment_method: 'PayPal',
                  payment_id: order.id,
                  status: 'paid',
                  order_type: 'Physical Copy of book'
                };
                
                return supabase.from('book_orders').insert([bookOrderData]);
              });
              
              const results = await Promise.all(orderPromises);
              const errors = results.filter(result => result.error);
              
              if (errors.length > 0) {
                console.error('Error saving book order:', errors[0].error);
              }
              
              // Clear cart after successful payment
              clearCart();
              navigate('/books/thankyou');
            } catch (error) {
              console.error('PayPal payment error:', error);
              alert('Payment processing failed. Please try again.');
            }
          },
          onError: (err) => {
            console.error('PayPal error:', err);
            alert('An error occurred with PayPal. Please try again.');
          }
        });
        
        paypalButtonsRef.current.render(paypalRef.current);
      }
    };

    // Check if PayPal SDK is already loaded
    if (window.paypal) {
      loadPayPal();
    } else {
      // Wait for PayPal SDK to load
      const checkPayPal = setInterval(() => {
        if (window.paypal) {
          clearInterval(checkPayPal);
          loadPayPal();
        }
      }, 100);
      
      return () => {
        clearInterval(checkPayPal);
        if (paypalRef.current) {
          paypalRef.current.innerHTML = '';
        }
        paypalButtonsRef.current = null;
      };
    }
    
    return () => {
      if (paypalRef.current) {
        paypalRef.current.innerHTML = '';
      }
      paypalButtonsRef.current = null;
    };
  }, [cart.length, getCartTotal, clearCart, navigate]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const orderId = `ORD-${Date.now()}`;
      const total = getCartTotal();
      
      // Create order for each book in cart
      const orderPromises = cart.map((item) => {
        const bookOrderData = {
          order_id: orderId,
          customer_name: form.name,
          email: form.email,
          mobile: form.mobile,
          address: form.address,
          city: form.city,
          country: form.country,
          book_title: item.title,
          book_key: item.key,
          book_image_url: item.img,
          quantity: item.quantity,
          unit_price: item.price,
          subtotal: item.price * item.quantity,
          total_amount: total,
          payment_method: null,
          status: 'pending',
          order_type: 'Physical Copy of book'
        };
        
        return supabase.from('book_orders').insert([bookOrderData]);
      });
      
      const results = await Promise.all(orderPromises);
      const errors = results.filter(result => result.error);
      
      if (errors.length > 0) {
        throw errors[0].error;
      }
      
      // Clear cart immediately after successful order
      clearCart();
      
      setTimeout(() => {
        setSubmitting(false);
        navigate('/books/thankyou');
      }, 1200);
    } catch (error) {
      console.error('Error saving book order:', error);
      // Even if there's an error, clear the cart if order was attempted
      // This prevents duplicate orders if user tries again
      clearCart();
      setTimeout(() => {
        setSubmitting(false);
        navigate('/books/thankyou');
      }, 1200);
    }
  };

  if (cart.length === 0) {
    return null;
  }

  const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 480px)').matches;

  return (
    <div className="main-bg">
      {!isMobile && (
        <header className="hero-section">
          <h1 className="site-title">Checkout</h1>
          <p className="site-tagline">Complete your purchase</p>
        </header>
      )}
      <div className="checkout-section">
        <div className="checkout-card">
          <h2>Order Summary</h2>
          <div className="checkout-items">
            {cart.map((item) => (
              <div key={item.key} className="checkout-item">
                <img src={item.img} alt={item.title} className="checkout-item-img" />
                <div className="checkout-item-details">
                  <h4>{item.title}</h4>
                  <p>Physical Copy of book</p>
                  <p>Quantity: {item.quantity} × ${item.price} = ${(item.price * item.quantity).toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="checkout-total">
            <h3>Total: ${getCartTotal().toFixed(2)}</h3>
          </div>
          
          <form className="checkout-form" onSubmit={handleSubmit}>
            <h3>Shipping Information</h3>
            <input name="name" type="text" placeholder="Your Name" value={form.name} onChange={handleChange} required />
            <input name="email" type="email" placeholder="Your Email" value={form.email} onChange={handleChange} required />
            <input name="mobile" type="tel" placeholder="Mobile Number" value={form.mobile} onChange={handleChange} required />
            <input name="address" type="text" placeholder="Street Address" value={form.address} onChange={handleChange} required />
            <input name="city" type="text" placeholder="City" value={form.city} onChange={handleChange} required />
            <input name="country" type="text" placeholder="Country" value={form.country} onChange={handleChange} required />
            
            <button type="submit" disabled={submitting} className="checkout-submit-btn">
              {submitting ? 'Processing...' : 'Place Order'}
            </button>
          </form>
          
          <div className="payment-divider">
            <span>OR</span>
          </div>
          
          <div className="paypal-container">
            <h3>Pay with PayPal</h3>
            <div ref={paypalRef} id="paypal-button-container"></div>
          </div>
        </div>
      </div>
      <footer className="footer">
        <p>&copy; {new Date().getFullYear()} Life Force Dynamics. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default BooksCheckout; 